import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { createPrintTranslator, getPrescriptionViaNames } from "@vet/print-core";
import { usePrintSettings } from "@/hooks/usePrintSettings";
import { buildPrescriptionPreviewHtml } from "@/lib/print/previewHtml";
import {
  buildDraftDocumentData,
  type DraftContext,
  type DraftHighlight,
} from "@/lib/prescriptions/editorDraft";
import type { MedicalPrescriptionInput } from "@/lib/validations/medical-prescription";

export interface PrescriptionDraft {
  values: MedicalPrescriptionInput;
  examItems?: Array<{ exam_name: string; instructions?: string | null }>;
  highlight: DraftHighlight | null;
}

interface DraftStore {
  context: DraftContext | null;
  draft: PrescriptionDraft | null;
  setDraft: (draft: PrescriptionDraft | null) => void;
  activeVia: number;
  setActiveVia: (index: number) => void;
}

const DraftStoreContext = createContext<DraftStore | null>(null);

export function PrescriptionDraftProvider({ context, children }: { context: DraftContext | null; children: ReactNode }) {
  const [draft, setDraft] = useState<PrescriptionDraft | null>(null);
  const [activeVia, setActiveVia] = useState(0);
  const value = useMemo(() => ({ context, draft, setDraft, activeVia, setActiveVia }), [context, draft, activeVia]);
  return <DraftStoreContext.Provider value={value}>{children}</DraftStoreContext.Provider>;
}

/** Editor side: publishes the in-progress prescription (debounced). Safe without a provider. */
export function usePublishPrescriptionDraft(draft: PrescriptionDraft | null, enabled = true) {
  const store = useContext(DraftStoreContext);
  const setDraft = store?.setDraft;
  const latest = useRef(draft);
  latest.current = draft;
  // Serialized so a new-but-equal object per render never re-publishes (avoids render loops).
  const key = JSON.stringify(draft);
  useEffect(() => {
    if (!setDraft || !enabled) return;
    const handle = window.setTimeout(() => setDraft(latest.current), 150);
    return () => window.clearTimeout(handle);
  }, [key, setDraft, enabled]);
  useEffect(() => {
    if (!enabled) return;
    return () => setDraft?.(null);
  }, [setDraft, enabled]);
}

const EMPTY_VALUES = {
  type: "standard",
  pet_id: "",
  clinic_id: "",
  control_regime: "none",
  buyer_mode: "blank",
  items: [],
} as unknown as MedicalPrescriptionInput;

export function usePrescriptionDraftPreview() {
  const store = useContext(DraftStoreContext);
  const { i18n } = useTranslation();
  const { data: printSettings } = usePrintSettings();
  const lang = i18n.language?.toLowerCase().startsWith("pt") ? "pt-BR" : "en";
  const activeVia = store?.activeVia ?? 0;
  const setActiveVia = store?.setActiveVia;
  const context = store?.context ?? null;
  const draft = store?.draft ?? null;

  const { html, viaNames } = useMemo(() => {
    if (!context) return { html: null as string | null, viaNames: [] as string[] };
    const data = buildDraftDocumentData({
      values: draft?.values ?? EMPTY_VALUES,
      examItems: draft?.examItems,
      context,
    });
    const names = getPrescriptionViaNames(data, createPrintTranslator(lang));
    const viaIndex = Math.min(activeVia, Math.max(names.length - 1, 0));
    return {
      html: buildPrescriptionPreviewHtml({
        printSettings: printSettings ?? null,
        data,
        lang,
        signed: false,
        viaIndex,
        draftHighlight: draft?.highlight ?? null,
      }),
      viaNames: names,
    };
  }, [context, draft, printSettings, lang, activeVia]);

  const changeVia = useCallback((index: number) => setActiveVia?.(index), [setActiveVia]);

  return { html, viaNames, activeVia, setActiveVia: changeVia, hasProvider: Boolean(store), lang };
}
